import { io, Socket } from "socket.io-client";

export interface RecordingConfig {
  engineUrl: string;
  organizationId: string;
  callId: string;
  userId: string;
  conversationId: string;
}

export interface CallRecordingHandle {
  stop: () => Promise<void>;
  isRecording: () => boolean;
}

const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/ogg;codecs=opus",
  "audio/webm",
  "audio/ogg",
];

function pickRecordingMimeType(): string {
  for (const c of MIME_CANDIDATES) {
    if (
      typeof MediaRecorder !== "undefined" &&
      MediaRecorder.isTypeSupported?.(c)
    )
      return c;
  }
  return "";
}

async function ensureAudioContextResumed(ctx: AudioContext): Promise<void> {
  if (ctx.state === "suspended") {
    await ctx.resume();
  }
}

/**
 * Starts recording the mixed local + remote audio and sends chunks to the media-recorder-engine.
 * Call the returned handle's stop() when done (e.g. on call end).
 */
export async function startCallRecording(
  localStream: MediaStream,
  remoteStream: MediaStream,
  config: RecordingConfig,
): Promise<CallRecordingHandle> {
  console.log("startCallRecording", localStream, remoteStream, config);
  const { engineUrl, organizationId, callId, userId, conversationId } = config;

  const socket: Socket = io(engineUrl, {
    transports: ["websocket", "polling"],
    reconnection: false,
  });

  await new Promise<void>((resolve, reject) => {
    const t = setTimeout(
      () => reject(new Error("Recording engine connection timeout")),
      15000,
    );
    socket.once("connect", () => {
      clearTimeout(t);
      resolve();
    });
    socket.once("connect_error", (err) => {
      clearTimeout(t);
      reject(err);
    });
  });
  console.log("socket connected");

  let ctx: AudioContext;
  try {
    ctx = new AudioContext({ sampleRate: 48000 });
  } catch {
    ctx = new AudioContext();
  }
  await ensureAudioContextResumed(ctx);

  const destination = ctx.createMediaStreamDestination();
  const localSource = ctx.createMediaStreamSource(localStream);
  const remoteSource = ctx.createMediaStreamSource(remoteStream);
  localSource.connect(destination);
  remoteSource.connect(destination);

  const mimeType = pickRecordingMimeType();
  const recorderOptions: MediaRecorderOptions = mimeType ? { mimeType } : {};
  recorderOptions.audioBitsPerSecond = 192000;

  const recorder = new MediaRecorder(destination.stream, recorderOptions);
  let recording = true;

  function releaseRecordingResources(): void {
    try {
      localSource.disconnect();
      remoteSource.disconnect();
      destination.stream.getTracks().forEach((t) => t.stop());
    } catch {
      // Ignore
    }
    try {
      if (ctx.state !== "closed") {
        ctx.close();
      }
    } catch {
      // Ignore
    }
  }
  console.log(
    "recording-start",
    organizationId,
    callId,
    userId,
    conversationId,
    mimeType,
  );
  socket.emit("recording-start", {
    organizationId,
    callId,
    userId,
    conversationId,
    mimeType: mimeType || "audio/webm",
  });

  recorder.ondataavailable = (ev: BlobEvent) => {
    if (!ev.data || ev.data.size === 0 || !recording) return;
    ev.data.arrayBuffer().then((buf) => {
      if (recording && socket.connected) {
        socket.emit("recording-chunk", { callId, userId, chunk: buf });
      }
    });
  };

  recorder.start(1000);

  socket.on("disconnect", () => {
    if (recording) {
      try {
        if (recorder.state !== "inactive") recorder.stop();
      } catch {
        // Ignore error
      }
      recording = false;
      releaseRecordingResources();
    }
  });

  return {
    isRecording: () => recording,

    stop: async () => {
      if (!recording) return;
      recording = false;
      try {
        if (recorder.state !== "inactive") recorder.stop();
      } catch {
        // Ignore error
      }
      releaseRecordingResources();
      if (socket.connected) {
        socket.emit("recording-stop", { callId });
        await new Promise((resolve) => setTimeout(resolve, 1000));
        socket.disconnect();
      }
    },
  };
}
